import { Autocomplete, Box, TextField, IconButton } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import Colors from "Constants/Color";
import { api } from "helpers/api_url";
import { useEffect, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import QuotationBagSelectionModal from "./QuotationBagSelectionModal";
import QuotationViewBagsModal from "./QuotationViewBagsModal";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import SubmitButton from "components/Buttons/SubmitButton";
import ActionButton from "components/Buttons/ActionButton";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

interface CategoryOption {
  category_id: string;
  category_name: string;
}

interface SubCategoryOption {
  sub_category_id: string;
  sub_category_name: string;
}

interface ItemOption {
  item_id: string;
  item_name: string;
  item_code: string;
  unit?: {
    unit_name: string;
  };
  selling_price?: string | number;
  hsn_code?: {
    gst_percent?: string | number;
  };
}

interface GstOption {
  gst_percent: string | number;
}

interface FormValues {
  organisation: any;
  department: any;
  team: any;
  user: any;
}

const QuotationItemTable = ({
  formik,
  mainFormik,
  editingItemId,
  setEditingItemId,
  bagQuantityValidation,
  setBagQuantityValidation,
  warehouseId,
}: {
  formik: any;
  mainFormik?: any;
  editingItemId?: number | null;
  setEditingItemId?: (id: number | null) => void;
  bagQuantityValidation?: Array<{
    inventory_item_id: string | number;
    available_quantity: number;
    issue_quantity: number;
  }>;
  setBagQuantityValidation?: (
    bags: Array<{
      inventory_item_id: string | number;
      available_quantity: number;
      issue_quantity: number;
    }>
  ) => void;
  warehouseId?: string | number;
}) => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    SubCategoryOption[]
  >([]);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [gstOptions, setGstOptions] = useState<GstOption[]>([]);
  const [showBagModal, setShowBagModal] = useState(false);
  const [selectedBags, setSelectedBags] = useState<any[]>([]);
  const [showViewBagsModal, setShowViewBagsModal] = useState(false);
  const [viewBagsForItem, setViewBagsForItem] = useState<any[]>([]);
  const [editingRowData, setEditingRowData] = useState<any | null>(null);

  useEffect(() => {
    Axios.get(`${api?.master?.category?.dropdown}`)
      .then((result) => {
        setCategoryOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch category dropdown");
      });
    Axios.get(`${api?.master?.gst?.dropdown}`)
      .then((result) => {
        setGstOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch GST dropdown");
      });
  }, []);

  // Auto-calculate Amount when Quantity or Rate changes
  useEffect(() => {
    const quantity = parseFloat(formik.values.quantity) || 0;
    const rate = parseFloat(formik.values.rate) || 0;
    const amount = quantity * rate;
    formik.setFieldValue("amount", amount.toFixed(2));
  }, [formik.values.quantity, formik.values.rate]);

  // Auto-calculate Discount Amount when Amount, Discount, or Discount Type changes
  useEffect(() => {
    const amount = parseFloat(formik.values.amount) || 0;
    const discount = parseFloat(formik.values.discount) || 0;
    const discountType = formik.values.discount_type || "%";

    let discountAmount = 0;
    if (discountType === "%") {
      discountAmount = (amount * discount) / 100;
    } else if (discountType === "₹") {
      discountAmount = discount;
    }

    formik.setFieldValue("discount_amount", discountAmount.toFixed(2));
  }, [
    formik.values.amount,
    formik.values.discount,
    formik.values.discount_type,
  ]);

  // Auto-calculate GST Amount and Total Amount when Net Amount or GST changes
  // Also recalculate when discount or discount_type changes since they affect discount_amount
  useEffect(() => {
    const amount = parseFloat(formik.values.amount) || 0;
    const discountAmount = parseFloat(formik.values.discount_amount) || 0;
    const netAmount = amount - discountAmount;

    // Handle both cases: GST as object with gst_percent or as a number
    let gstPercent = 0;
    if (formik.values.gst) {
      if (
        typeof formik.values.gst === "object" &&
        formik.values.gst.gst_percent !== undefined
      ) {
        gstPercent = parseFloat(String(formik.values.gst.gst_percent)) || 0;
      } else if (
        typeof formik.values.gst === "number" ||
        (typeof formik.values.gst === "string" &&
          !isNaN(Number(formik.values.gst)))
      ) {
        gstPercent = parseFloat(String(formik.values.gst)) || 0;
      }
    }

    const gstAmount = (netAmount * gstPercent) / 100;
    const totalAmount = netAmount + gstAmount;

    formik.setFieldValue("gst_amount", gstAmount.toFixed(2));
    formik.setFieldValue("total_amount", totalAmount.toFixed(2));
  }, [
    formik.values.amount,
    formik.values.discount_amount,
    formik.values.discount,
    formik.values.discount_type,
    formik.values.gst,
  ]);

  const fetchSubCategoryDropdownByCategory = (category_id: string) => {
    Axios.get(
      `${api?.master?.sub_category?.fetchSubCategoryDropdownByCategoryId}/${category_id}`
    )
      .then((result) => {
        setSubCategoryOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch sub category dropdown");
      });
  };
  const fetchItemOptions = (category_id: string, sub_category_id: string) => {
    Axios.get(
      `/master/item/dropdown/category/${category_id}/sub-category/${sub_category_id}`
    )
      .then((result) => {
        setItemOptions(result?.data?.data || []);
      })
      .catch((err) => {
        toast.error("Failed to fetch sub category dropdown");
      });
  };

  const handleEditItem = async (item: any) => {
    if (setEditingItemId) {
      setEditingItemId(item.id);
    }
    // Ensure proper structure for editingRowData
    const editingData = {
      ...item,
      category: item.category || null,
      sub_category: item.sub_category || null,
      item: item.item || null,
    };
    setEditingRowData(editingData);

    // Reset add form to prevent autofilling when editing
    formik.resetForm();
    setSelectedBags([]);

    // Load subcategories if category exists
    if (item.category && item.category.category_id) {
      await fetchSubCategoryDropdownByCategory(item.category.category_id);
    } else {
      setSubCategoryOptions([]);
      setItemOptions([]);
    }

    // Load items if subcategory exists
    if (
      item.sub_category &&
      item.sub_category.sub_category_id &&
      item.category &&
      item.category.category_id
    ) {
      await fetchItemOptions(
        item.category.category_id,
        item.sub_category.sub_category_id
      );
    } else {
      setItemOptions([]);
    }
  };

  const handleSaveEdit = () => {
    console.log("editingItemId : ", editingItemId);
    // if (!editingRowData || !editingItemId) return;

    // Validate that at least one bag is selected
    if (
      !editingRowData.bags ||
      !Array.isArray(editingRowData.bags) ||
      editingRowData.bags.length === 0
    ) {
      toast.error("At least one bag must be selected for this item");
      return;
    }

    // Get the old item to remove its bags from bagQuantityValidation
    const currentItems = mainFormik.values.items || [];
    const oldItem = currentItems.find((item: any) => item.id === editingItemId);

    // Start with current bagQuantityValidation state
    let updatedBagValidation = [...(bagQuantityValidation || [])];

    // Remove old bags from bagQuantityValidation
    if (oldItem?.bags && oldItem.bags.length > 0 && setBagQuantityValidation) {
      oldItem.bags.forEach((bag: any) => {
        const bagIndex = updatedBagValidation.findIndex(
          (b) => String(b.inventory_item_id) === String(bag.inventory_item_id)
        );
        if (bagIndex !== -1) {
          updatedBagValidation[bagIndex].issue_quantity -=
            parseFloat(String(bag.issue_quantity)) || 0;
          // Remove if issue_quantity becomes 0 or negative
          if (updatedBagValidation[bagIndex].issue_quantity <= 0) {
            updatedBagValidation.splice(bagIndex, 1);
          }
        }
      });
    }

    // Add new bags to bagQuantityValidation
    if (
      editingRowData.bags &&
      editingRowData.bags.length > 0 &&
      setBagQuantityValidation
    ) {
      let validationError = false;

      // First, validate all bags before updating state
      for (const bag of editingRowData.bags) {
        const inventoryItemId = bag.inventory_item_id;
        const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
        const availableQty = parseFloat(String(bag.available_quantity)) || 0;

        const existingBagIndex = updatedBagValidation.findIndex(
          (b) => String(b.inventory_item_id) === String(inventoryItemId)
        );

        if (existingBagIndex !== -1) {
          // Bag exists, check if total would exceed available
          const newTotalIssueQty =
            updatedBagValidation[existingBagIndex].issue_quantity + issueQty;

          if (newTotalIssueQty > availableQty) {
            toast.error(
              `Bag is used for another item also and issued quantity (${newTotalIssueQty.toFixed(
                2
              )}) is going greater than available quantity (${availableQty.toFixed(
                2
              )})`
            );
            validationError = true;
            break;
          }
        } else {
          // Bag doesn't exist, validate new entry
          if (issueQty > availableQty) {
            toast.error(
              `Issued quantity (${issueQty.toFixed(
                2
              )}) cannot exceed available quantity (${availableQty.toFixed(2)})`
            );
            validationError = true;
            break;
          }
        }
      }

      // If validation failed, restore old bags and return
      if (validationError) {
        // Restore the original bagQuantityValidation (before we removed old bags)
        if (
          oldItem?.bags &&
          oldItem.bags.length > 0 &&
          setBagQuantityValidation &&
          bagQuantityValidation
        ) {
          const restoredBagValidation = [...bagQuantityValidation];
          oldItem.bags.forEach((bag: any) => {
            const inventoryItemId = bag.inventory_item_id;
            const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
            const availableQty =
              parseFloat(String(bag.available_quantity)) || 0;

            const existingBagIndex = restoredBagValidation.findIndex(
              (b) => String(b.inventory_item_id) === String(inventoryItemId)
            );

            if (existingBagIndex !== -1) {
              restoredBagValidation[existingBagIndex].issue_quantity +=
                issueQty;
            } else {
              restoredBagValidation.push({
                inventory_item_id: inventoryItemId,
                available_quantity: availableQty,
                issue_quantity: issueQty,
              });
            }
          });
          setBagQuantityValidation(restoredBagValidation);
        }
        return;
      }

      // All validations passed, update the state
      editingRowData.bags.forEach((bag: any) => {
        const inventoryItemId = bag.inventory_item_id;
        const issueQty = parseFloat(String(bag.issue_quantity)) || 0;
        const availableQty = parseFloat(String(bag.available_quantity)) || 0;

        const existingBagIndex = updatedBagValidation.findIndex(
          (b) => String(b.inventory_item_id) === String(inventoryItemId)
        );

        if (existingBagIndex !== -1) {
          // Bag exists, append issue quantity
          updatedBagValidation[existingBagIndex].issue_quantity += issueQty;
        } else {
          // Bag doesn't exist, add new entry
          updatedBagValidation.push({
            inventory_item_id: inventoryItemId,
            available_quantity: availableQty,
            issue_quantity: issueQty,
          });
        }
      });
    }

    // Update bagQuantityValidation state if we modified it
    if (setBagQuantityValidation) {
      setBagQuantityValidation(updatedBagValidation);
    }

    // Update the items array with the edited item
    const updatedItems = currentItems.map((item: any) =>
      item.id === editingItemId ? editingRowData : item
    );
    mainFormik.setFieldValue("items", updatedItems);

    // Clear editing state
    if (setEditingItemId) {
      setEditingItemId(null);
    }
    setEditingRowData(null);

    toast.success("Item updated successfully");
  };

  const handleCancelEdit = () => {
    if (setEditingItemId) {
      setEditingItemId(null);
    }
    setEditingRowData(null);
    formik.resetForm();
    setSelectedBags([]);
  };

  const handleEditFieldChange = (key: string, value: any) => {
    if (!editingRowData) return;
    const updated = { ...editingRowData, [key]: value };

    // Clear bags when category, sub_category, or item changes (but NOT when quantity changes)
    // This allows users to edit quantity without losing their bag selections
    // If category/sub_category/item changes, bags must be cleared as they're item-specific
    const shouldClearBags =
      key === "category" || key === "sub_category" || key === "item";
    if (shouldClearBags && updated.bags && updated.bags.length > 0) {
      // Remove bags from bagQuantityValidation
      if (setBagQuantityValidation && bagQuantityValidation) {
        const updatedBagValidation = [...bagQuantityValidation];
        updated.bags.forEach((bag: any) => {
          const bagIndex = updatedBagValidation.findIndex(
            (b) => String(b.inventory_item_id) === String(bag.inventory_item_id)
          );
          if (bagIndex !== -1) {
            updatedBagValidation[bagIndex].issue_quantity -=
              parseFloat(String(bag.issue_quantity)) || 0;
            // Remove if issue_quantity becomes 0 or negative
            if (updatedBagValidation[bagIndex].issue_quantity <= 0) {
              updatedBagValidation.splice(bagIndex, 1);
            }
          }
        });
        setBagQuantityValidation(updatedBagValidation);
      }
      updated.bags = [];
    }

    // Recalculate amount if quantity or rate changes
    if (key === "quantity" || key === "rate") {
      const quantity = parseFloat(String(updated.quantity)) || 0;
      const rate = parseFloat(String(updated.rate)) || 0;
      updated.amount = (quantity * rate).toFixed(2);
    }

    // Recalculate discount amount if amount, discount, or discount_type changes
    if (key === "amount" || key === "discount" || key === "discount_type") {
      const amount = parseFloat(String(updated.amount)) || 0;
      const discount = parseFloat(String(updated.discount)) || 0;
      const discountType = updated.discount_type || "%";
      let discountAmount = 0;
      if (discountType === "%") {
        discountAmount = (amount * discount) / 100;
      } else if (discountType === "₹") {
        discountAmount = discount;
      }
      updated.discount_amount = discountAmount.toFixed(2);
    }

    // Recalculate GST amount and total amount when amount, discount_amount, discount, discount_type, or gst changes
    // Also recalculate after discount changes since it affects discount_amount
    if (
      key === "amount" ||
      key === "discount_amount" ||
      key === "discount" ||
      key === "discount_type" ||
      key === "gst"
    ) {
      const amount = parseFloat(String(updated.amount)) || 0;
      const discountAmount = parseFloat(String(updated.discount_amount)) || 0;
      const netAmount = amount - discountAmount;

      // Handle both cases: GST as object with gst_percent or as a number
      let gstPercent = 0;
      if (updated.gst) {
        if (
          typeof updated.gst === "object" &&
          updated.gst.gst_percent !== undefined
        ) {
          gstPercent = parseFloat(String(updated.gst.gst_percent)) || 0;
        } else if (
          typeof updated.gst === "number" ||
          (typeof updated.gst === "string" && !isNaN(Number(updated.gst)))
        ) {
          gstPercent = parseFloat(String(updated.gst)) || 0;
        }
      }

      const gstAmount = (netAmount * gstPercent) / 100;
      const totalAmount = netAmount + gstAmount;
      updated.gst_amount = gstAmount.toFixed(2);
      updated.total_amount = totalAmount.toFixed(2);
    }

    setEditingRowData(updated);
  };

  return (
    <>
      <style>
        {`
                    .sticky-column {
                        position: relative;
                    }
                    .sticky-column::before {
                        content: '';
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        width: 1px;
                        background-color: #dee2e6;
                        z-index: 12;
                    }
                    thead .sticky-column::before {
                        z-index: 13;
                    }
                `}
      </style>
      <div className="mb-4">
        <Table hover responsive className="align-middle text-center">
          <thead>
            <tr>
              <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                Sr No.
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Category
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Sub Category
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Item Name
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Item Code
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Unit
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Quantity
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "130px" }}>
                Bags
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Rate
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "250px" }}>
                Discount
              </th>
              {/* <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>Discount Type</th> */}
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Discount Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                GST%
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                GST Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Total Amount
              </th>
              <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                Remarks
              </th>
              <th
                className="sticky-column"
                style={{
                  ...tableStyles.headerAlt,
                  ...tableStyles.actionHeader,
                  minWidth: "100px",
                }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Input Row */}
            <tr>
              <td style={tableStyles.cell}></td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={categoryOptions || []}
                  autoHighlight
                  value={formik?.values?.category}
                  onChange={(_, val) => {
                    formik.setFieldValue("category", val);
                    if (val) {
                      fetchSubCategoryDropdownByCategory(val?.category_id);
                    } else {
                      // Clear all dependent fields when category is removed
                      setSubCategoryOptions([]);
                      setItemOptions([]);
                      formik.setFieldValue("sub_category", null);
                      formik.setFieldValue("item", null);
                      formik.setFieldValue("item_code", "");
                      formik.setFieldValue("unit", "");
                      formik.setFieldValue("quantity", "");
                      formik.setFieldValue("bags", []);
                      setSelectedBags([]);
                      formik.setFieldValue("rate", "");
                      formik.setFieldValue("amount", "");
                      formik.setFieldValue("discount", "");
                      formik.setFieldValue("discount_type", "%");
                      formik.setFieldValue("discount_amount", "");
                      formik.setFieldValue("gst", null);
                      formik.setFieldValue("gst_amount", "");
                      formik.setFieldValue("total_amount", "");
                      formik.setFieldValue("remark", "");
                    }
                    // Cancel edit mode when category changes
                    if (editingItemId !== null && setEditingItemId) {
                      setEditingItemId(null);
                      setEditingRowData(null);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("category", true)}
                  getOptionLabel={(option) =>
                    option ? option?.category_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.category_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Category"
                      error={
                        formik.touched.category &&
                        Boolean(formik.errors.category)
                      }
                      helperText={
                        formik.touched.category &&
                        typeof formik.errors.category === "string"
                          ? formik.errors.category
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={subCategoryOptions || []}
                  autoHighlight
                  disabled={!formik?.values?.category}
                  value={formik?.values?.sub_category}
                  onChange={(_, val) => {
                    formik.setFieldValue("sub_category", val);
                    if (
                      val &&
                      formik.values.category &&
                      formik.values.category.category_id
                    ) {
                      fetchItemOptions(
                        formik.values.category.category_id,
                        val?.sub_category_id
                      );
                    } else {
                      // Clear all dependent fields when sub_category is removed
                      setItemOptions([]);
                      formik.setFieldValue("item", null);
                      formik.setFieldValue("item_code", "");
                      formik.setFieldValue("unit", "");
                      formik.setFieldValue("quantity", "");
                      formik.setFieldValue("bags", []);
                      setSelectedBags([]);
                      formik.setFieldValue("rate", "");
                      formik.setFieldValue("amount", "");
                      formik.setFieldValue("discount", "");
                      formik.setFieldValue("discount_type", "%");
                      formik.setFieldValue("discount_amount", "");
                      formik.setFieldValue("gst", null);
                      formik.setFieldValue("gst_amount", "");
                      formik.setFieldValue("total_amount", "");
                      formik.setFieldValue("remark", "");
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("sub_category", true)}
                  getOptionLabel={(option) =>
                    option ? option?.sub_category_name : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.sub_category_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Sub Category"
                      error={
                        formik.touched.sub_category &&
                        Boolean(formik.errors.sub_category)
                      }
                      helperText={
                        formik.touched.sub_category &&
                        typeof formik.errors.sub_category === "string"
                          ? formik.errors.sub_category
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={itemOptions || []}
                  autoHighlight
                  disabled={!formik?.values?.sub_category}
                  value={formik?.values?.item}
                  onChange={(_, val) => {
                    if (val) {
                      formik.setFieldValue("item", val);
                      formik.setFieldValue("item_code", val?.item_code);
                      formik.setFieldValue("unit", val?.unit?.unit_name);
                      // Find matching GST object from gstOptions
                      const gstPercent = val?.hsn_code?.gst_percent;
                      if (gstPercent !== undefined && gstPercent !== null) {
                        const matchingGst = gstOptions.find(
                          (g) =>
                            String(g.gst_percent) ===
                            String(gstPercent?.gst_percent)
                        );
                        formik.setFieldValue("gst", matchingGst || null);
                      } else {
                        formik.setFieldValue("gst", null);
                      }
                      formik.setFieldValue("rate", val?.selling_price);
                    } else {
                      // Clear all item-related fields when item is removed
                      formik.setFieldValue("item", val);
                      formik.setFieldValue("item_code", "");
                      formik.setFieldValue("unit", "");
                      formik.setFieldValue("quantity", "");
                      formik.setFieldValue("bags", []);
                      setSelectedBags([]);
                      formik.setFieldValue("rate", "");
                      formik.setFieldValue("amount", "");
                      formik.setFieldValue("discount", "");
                      formik.setFieldValue("discount_type", "%");
                      formik.setFieldValue("discount_amount", "");
                      formik.setFieldValue("gst", null);
                      formik.setFieldValue("gst_amount", "");
                      formik.setFieldValue("total_amount", "");
                      formik.setFieldValue("remark", "");
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("item", true)}
                  getOptionLabel={(option) => (option ? option?.item_name : "")}
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.item_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Item Name"
                      error={formik.touched.item && Boolean(formik.errors.item)}
                      helperText={
                        formik.touched.item &&
                        typeof formik.errors.item === "string"
                          ? formik.errors.item
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.item_code || ""}
                  disabled
                  placeholder="Code"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.unit || ""}
                  disabled
                  placeholder="Unit"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  type="text"
                  placeholder="Enter Quantity"
                  onKeyDown={(e) => {
                    if (
                      [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "Tab",
                      ].includes(e.key)
                    ) {
                      return; // allow control keys
                    }
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault(); // block all non-digits
                    }
                  }}
                  value={formik.values.quantity}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("quantity", value);
                    // Clear bags when quantity changes
                    if (formik.values.bags && formik.values.bags.length > 0) {
                      // Remove bags from bagQuantityValidation
                      if (setBagQuantityValidation && bagQuantityValidation) {
                        const updatedBagValidation = [...bagQuantityValidation];
                        formik.values.bags.forEach((bag: any) => {
                          const bagIndex = updatedBagValidation.findIndex(
                            (b) =>
                              String(b.inventory_item_id) ===
                              String(bag.inventory_item_id)
                          );
                          if (bagIndex !== -1) {
                            updatedBagValidation[bagIndex].issue_quantity -=
                              parseFloat(String(bag.issue_quantity)) || 0;
                            // Remove if issue_quantity becomes 0 or negative
                            if (
                              updatedBagValidation[bagIndex].issue_quantity <= 0
                            ) {
                              updatedBagValidation.splice(bagIndex, 1);
                            }
                          }
                        });
                        setBagQuantityValidation(updatedBagValidation);
                      }
                      formik.setFieldValue("bags", []);
                      setSelectedBags([]);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.quantity && formik.touched.quantity}
                  helperText={
                    formik.touched.quantity &&
                    typeof formik.errors.quantity === "string"
                      ? formik.errors.quantity
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <div>
                  <ActionButton
                    label={
                      formik.values.bags && formik.values.bags.length > 0
                        ? `${formik.values.bags.length} Bag(s)`
                        : "Select"
                    }
                    icon="bi bi-bag"
                    handleClick={() => {
                      if (!warehouseId) {
                        toast.error(
                          "Please select Dispatch From warehouse before selecting bags"
                        );
                        return;
                      }
                      if (
                        !formik.values.quantity ||
                        parseFloat(formik.values.quantity) <= 0
                      ) {
                        toast.error("Please enter a valid quantity first");
                        return;
                      }
                      // Initialize selected bags from formik if they exist
                      // Add id field for modal internal use
                      if (formik.values.bags && formik.values.bags.length > 0) {
                        const bagsWithId = formik.values.bags.map(
                          (bag: any, idx: number) => ({
                            ...bag,
                            id: bag.id || `bag-${Date.now()}-${idx}`,
                          })
                        );
                        setSelectedBags(bagsWithId);
                      } else {
                        setSelectedBags([]);
                      }
                      setShowBagModal(true);
                    }}
                    variant="primary"
                    disabled={!formik?.values?.item || !warehouseId}
                  />
                  {formik.touched.bags && formik.errors.bags && (
                    <div
                      className="text-danger"
                      style={{ fontSize: "0.75rem", marginTop: "4px" }}
                    >
                      {typeof formik.errors.bags === "string"
                        ? formik.errors.bags
                        : "At least one bag is required"}
                    </div>
                  )}
                  {formik.values.bags && formik.values.bags.length > 0 && (
                    <div
                      className="text-success"
                      style={{ fontSize: "0.75rem", marginTop: "4px" }}
                    >
                      {formik.values.bags.length} bag(s) selected
                    </div>
                  )}
                </div>
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  type="text"
                  placeholder="Enter Rate"
                   onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        return; // allow control keys
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault(); // block all non-digits
                      }
                    }}
                  value={formik.values.rate}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("rate", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.rate && formik.touched.rate}
                  helperText={
                    formik.touched.rate &&
                    typeof formik.errors.rate === "string"
                      ? formik.errors.rate
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "flex-start",
                  }}
                >
                  <TextField
                    size="small"
                    type="text"
                    placeholder="Enter Discount"
                    value={formik.values.discount}
                    // onChange={(e) => {
                    //     const value = e.target.value;
                    //     formik.setFieldValue("discount", value);
                    // }}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numberRegex = /^\d*\.?\d*$/;

                      if (value === "" || numberRegex.test(value)) {
                        formik.setFieldValue("discount", value);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={!!formik.errors.discount && formik.touched.discount}
                    helperText={
                      formik.touched.discount &&
                      typeof formik.errors.discount === "string"
                        ? formik.errors.discount
                        : undefined
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      flex: 1,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                  <Autocomplete
                    size="small"
                    options={["%", "₹"]}
                    autoHighlight
                    disableClearable
                    value={formik?.values?.discount_type}
                    onChange={(_, val) => {
                      formik.setFieldValue("discount_type", val);
                    }}
                    onBlur={() => formik.setFieldTouched("discount_type", true)}
                    getOptionLabel={(option) => (option ? option : "")}
                    renderOption={(props, option) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (opt) => opt?.toString() || ""
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={
                          formik.touched.discount_type &&
                          Boolean(formik.errors.discount_type)
                        }
                        helperText={
                          formik.touched.discount_type &&
                          typeof formik.errors.discount_type === "string"
                            ? formik.errors.discount_type
                            : undefined
                        }
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          width: "80px",
                          "& .MuiInputBase-root": {
                            height: "32px",
                            maxWidth: "60px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 5px",
                            height: "20px",
                            maxWidth: "60px",
                          },
                        }}
                      />
                    )}
                    ListboxProps={{
                      ...autocompleteDropdownStyles.ListboxProps,
                      style: {
                        maxHeight: "200px",
                        overflow: "auto",
                        zIndex: 100,
                      },
                    }}
                    componentsProps={{
                      popper: {
                        style: {
                          zIndex: 100,
                        },
                        modifiers: [
                          {
                            name: "preventOverflow",
                            enabled: true,
                          },
                        ],
                      },
                    }}
                  />
                </div>
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.discount_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <Autocomplete
                  size="small"
                  options={gstOptions || []}
                  autoHighlight
                  value={formik?.values?.gst}
                  onChange={(_, val) => {
                    formik.setFieldValue("gst", val);
                  }}
                  onBlur={() => formik.setFieldTouched("gst", true)}
                  getOptionLabel={(option) =>
                    option && option?.gst_percent
                      ? String(option.gst_percent)
                      : ""
                  }
                  renderOption={(props, option: any) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (opt?.gst_percent ? String(opt.gst_percent) : "")
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select GST"
                      error={formik.touched.gst && Boolean(formik.errors.gst)}
                      helperText={
                        formik.touched.gst &&
                        typeof formik.errors.gst === "string"
                          ? formik.errors.gst
                          : undefined
                      }
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  )}
                  ListboxProps={{
                    ...autocompleteDropdownStyles.ListboxProps,
                    style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      zIndex: 100,
                    },
                  }}
                  componentsProps={{
                    popper: {
                      style: {
                        zIndex: 100,
                      },
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true,
                        },
                      ],
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.gst_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  value={formik.values.total_amount || ""}
                  disabled
                  placeholder="Auto Calculated"
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td style={tableStyles.cell}>
                <TextField
                  size="small"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("remark", value);
                  }}
                  onBlur={formik.handleBlur}
                  error={!!formik.errors.remark && formik.touched.remark}
                  helperText={
                    formik.touched.remark &&
                    typeof formik.errors.remark === "string"
                      ? formik.errors.remark
                      : undefined
                  }
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
              <td className="sticky-column" style={tableStyles.actionCell}>
                <div style={tableStyles.actionButtonContainer}>
                  <SubmitButton
                    isSubmitting={false}
                    onClick={() => formik?.handleSubmit()}
                  />
                </div>
              </td>
            </tr>

            {/* Existing Rows */}
            {mainFormik?.values?.items?.length === 0 ? (
              <tr>
                <td
                  colSpan={16}
                  style={{ ...tableStyles.cell, textAlign: "center" }}
                  className="py-2"
                >
                  No Items added. Please add at least one item.
                </td>
                <td
                  style={tableStyles.actionCell}
                  className="sticky-column"
                ></td>
              </tr>
            ) : (
              mainFormik?.values?.items?.map((row: any, idx: number) => {
                const isEditing = editingItemId === row.id;
                const displayData =
                  isEditing && editingRowData ? editingRowData : row;

                return (
                  <tr
                    key={row.id || idx}
                    style={isEditing ? tableStyles.rowEditing : {}}
                  >
                    <td style={tableStyles.cell}>{idx + 1}</td>

                    {/* Category */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={categoryOptions || []}
                          getOptionLabel={(opt) => opt?.category_name || ""}
                          value={
                            categoryOptions.find(
                              (c) =>
                                c.category_id ===
                                displayData.category?.category_id
                            ) || null
                          }
                          disabled={true}
                          onChange={async (_, value) => {
                            if (value) {
                              handleEditFieldChange("category", value);
                              handleEditFieldChange("sub_category", null);
                              handleEditFieldChange("item", null);
                              handleEditFieldChange("item_code", "");
                              handleEditFieldChange("unit", "");
                              handleEditFieldChange("quantity", "");
                              handleEditFieldChange("bags", []);
                              handleEditFieldChange("rate", "");
                              handleEditFieldChange("amount", "");
                              handleEditFieldChange("discount", "");
                              handleEditFieldChange("discount_type", "%");
                              handleEditFieldChange("discount_amount", "");
                              handleEditFieldChange("gst", null);
                              handleEditFieldChange("gst_amount", "");
                              handleEditFieldChange("total_amount", "");
                              handleEditFieldChange("remark", "");
                              if (value.category_id) {
                                await fetchSubCategoryDropdownByCategory(
                                  value.category_id
                                );
                              }
                            } else {
                              // When category is cleared, clear all dependent fields
                              handleEditFieldChange("category", null);
                              handleEditFieldChange("sub_category", null);
                              handleEditFieldChange("item", null);
                              handleEditFieldChange("item_code", "");
                              handleEditFieldChange("unit", "");
                              handleEditFieldChange("quantity", "");
                              handleEditFieldChange("bags", []);
                              handleEditFieldChange("rate", "");
                              handleEditFieldChange("amount", "");
                              handleEditFieldChange("discount", "");
                              handleEditFieldChange("discount_type", "%");
                              handleEditFieldChange("discount_amount", "");
                              handleEditFieldChange("gst", null);
                              handleEditFieldChange("gst_amount", "");
                              handleEditFieldChange("total_amount", "");
                              handleEditFieldChange("remark", "");
                              setSubCategoryOptions([]);
                              setItemOptions([]);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                "& .MuiInputBase-root": {
                                  height: "32px",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "6px 12px",
                                  height: "20px",
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt?.category_name || ""
                            )
                          }
                          ListboxProps={{
                            ...autocompleteDropdownStyles.ListboxProps,
                            style: {
                              maxHeight: "200px",
                              overflow: "auto",
                              zIndex: 100,
                            },
                          }}
                          componentsProps={{
                            popper: {
                              style: {
                                zIndex: 100,
                              },
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  enabled: true,
                                },
                              ],
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.category?.category_name || "-"}</span>
                      )}
                    </td>

                    {/* Sub Category */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={subCategoryOptions || []}
                          getOptionLabel={(opt) => opt?.sub_category_name || ""}
                          value={
                            subCategoryOptions.find(
                              (s) =>
                                s.sub_category_id ===
                                displayData.sub_category?.sub_category_id
                            ) || null
                          }
                          disabled={true}
                          onChange={async (_, value) => {
                            if (value) {
                              handleEditFieldChange("sub_category", value);
                              handleEditFieldChange("item", null);
                              handleEditFieldChange("item_code", "");
                              handleEditFieldChange("unit", "");
                              handleEditFieldChange("quantity", "");
                              handleEditFieldChange("bags", []);
                              handleEditFieldChange("rate", "");
                              handleEditFieldChange("amount", "");
                              handleEditFieldChange("discount", "");
                              handleEditFieldChange("discount_type", "%");
                              handleEditFieldChange("discount_amount", "");
                              handleEditFieldChange("gst", null);
                              handleEditFieldChange("gst_amount", "");
                              handleEditFieldChange("total_amount", "");
                              handleEditFieldChange("remark", "");
                              if (
                                value.sub_category_id &&
                                displayData.category &&
                                displayData.category.category_id
                              ) {
                                await fetchItemOptions(
                                  displayData.category.category_id,
                                  value.sub_category_id
                                );
                              }
                            } else {
                              // When sub_category is cleared, clear all dependent fields
                              handleEditFieldChange("sub_category", null);
                              handleEditFieldChange("item", null);
                              handleEditFieldChange("item_code", "");
                              handleEditFieldChange("unit", "");
                              handleEditFieldChange("quantity", "");
                              handleEditFieldChange("bags", []);
                              handleEditFieldChange("rate", "");
                              handleEditFieldChange("amount", "");
                              handleEditFieldChange("discount", "");
                              handleEditFieldChange("discount_type", "%");
                              handleEditFieldChange("discount_amount", "");
                              handleEditFieldChange("gst", null);
                              handleEditFieldChange("gst_amount", "");
                              handleEditFieldChange("total_amount", "");
                              handleEditFieldChange("remark", "");
                              setItemOptions([]);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                "& .MuiInputBase-root": {
                                  height: "32px",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "6px 12px",
                                  height: "20px",
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt?.sub_category_name || ""
                            )
                          }
                          ListboxProps={{
                            ...autocompleteDropdownStyles.ListboxProps,
                            style: {
                              maxHeight: "200px",
                              overflow: "auto",
                              zIndex: 100,
                            },
                          }}
                          componentsProps={{
                            popper: {
                              style: {
                                zIndex: 100,
                              },
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  enabled: true,
                                },
                              ],
                            },
                          }}
                        />
                      ) : (
                        <span>
                          {row?.sub_category?.sub_category_name || "-"}
                        </span>
                      )}
                    </td>

                    {/* Item Name */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={itemOptions || []}
                          getOptionLabel={(opt) => opt?.item_name || ""}
                          value={
                            itemOptions.find(
                              (i) => i.item_id === displayData.item?.item_id
                            ) || null
                          }
                          disabled={true}
                          onChange={(_, value) => {
                            if (value) {
                              handleEditFieldChange("item", value);
                              handleEditFieldChange(
                                "item_code",
                                value.item_code || ""
                              );
                              handleEditFieldChange(
                                "unit",
                                value.unit?.unit_name || ""
                              );
                              handleEditFieldChange(
                                "rate",
                                value.selling_price || ""
                              );
                              // Find matching GST object from gstOptions
                              const gstPercent = value.hsn_code?.gst_percent;
                              if (
                                gstPercent !== undefined &&
                                gstPercent !== null
                              ) {
                                const matchingGst = gstOptions.find(
                                  (g) =>
                                    String(g.gst_percent) === String(gstPercent)
                                );
                                handleEditFieldChange(
                                  "gst",
                                  matchingGst || null
                                );
                              } else {
                                handleEditFieldChange("gst", null);
                              }
                            } else {
                              // When item is cleared, clear all item-related fields
                              handleEditFieldChange("item", null);
                              handleEditFieldChange("item_code", "");
                              handleEditFieldChange("unit", "");
                              handleEditFieldChange("quantity", "");
                              handleEditFieldChange("bags", []);
                              handleEditFieldChange("rate", "");
                              handleEditFieldChange("amount", "");
                              handleEditFieldChange("discount", "");
                              handleEditFieldChange("discount_type", "%");
                              handleEditFieldChange("discount_amount", "");
                              handleEditFieldChange("gst", null);
                              handleEditFieldChange("gst_amount", "");
                              handleEditFieldChange("total_amount", "");
                              handleEditFieldChange("remark", "");
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                "& .MuiInputBase-root": {
                                  height: "32px",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "6px 12px",
                                  height: "20px",
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt?.item_name || ""
                            )
                          }
                          ListboxProps={{
                            ...autocompleteDropdownStyles.ListboxProps,
                            style: {
                              maxHeight: "200px",
                              overflow: "auto",
                              zIndex: 100,
                            },
                          }}
                          componentsProps={{
                            popper: {
                              style: {
                                zIndex: 100,
                              },
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  enabled: true,
                                },
                              ],
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.item?.item_name || "-"}</span>
                      )}
                    </td>

                    {/* Item Code */}
                    <td style={tableStyles.cell}>
                      <span>
                        {displayData?.item_code || row?.item_code || "-"}
                      </span>
                    </td>

                    {/* Unit */}
                    <td style={tableStyles.cell}>
                      <span>
                        {displayData?.unit?.unit_name ||
                          displayData?.unit ||
                          row?.unit?.unit_name ||
                          row?.unit ||
                          "-"}
                      </span>
                    </td>

                    {/* Quantity */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={displayData.quantity || ""}
                          onChange={(e) =>
                            handleEditFieldChange("quantity", e.target.value)
                          }
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.quantity || "-"}</span>
                      )}
                    </td>

                    {/* Bags */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <div>
                          <ActionButton
                            label={
                              displayData.bags && displayData.bags.length > 0
                                ? `${displayData.bags.length} Bag(s)`
                                : "Select"
                            }
                            icon="bi bi-bag"
                            handleClick={() => {
                              if (
                                !displayData.quantity ||
                                parseFloat(displayData.quantity) <= 0
                              ) {
                                toast.error(
                                  "Please enter a valid quantity first"
                                );
                                return;
                              }
                              if (!warehouseId) {
                                toast.error(
                                  "Please select Dispatch From warehouse before selecting bags"
                                );
                                return;
                              }
                              if (
                                !displayData.category ||
                                !displayData.sub_category ||
                                !displayData.item
                              ) {
                                toast.error(
                                  "Please select Category, Sub Category, and Item Name before selecting bags"
                                );
                                return;
                              }
                              // Initialize selected bags from editingRowData if they exist
                              if (
                                displayData.bags &&
                                displayData.bags.length > 0
                              ) {
                                const bagsWithId = displayData.bags.map(
                                  (bag: any, idx: number) => ({
                                    ...bag,
                                    id: bag.id || `bag-${Date.now()}-${idx}`,
                                  })
                                );
                                setSelectedBags(bagsWithId);
                              } else {
                                setSelectedBags([]);
                              }
                              setShowBagModal(true);
                            }}
                            variant="primary"
                            disabled={
                              !displayData.category ||
                              !displayData.sub_category ||
                              !displayData.item ||
                              !warehouseId
                            }
                          />
                          {displayData.bags && displayData.bags.length > 0 && (
                            <div
                              className="text-success"
                              style={{ fontSize: "0.75rem", marginTop: "4px" }}
                            >
                              {displayData.bags.length} bag(s) selected
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ textAlign: "center" }}>
                          {row?.bags && row.bags.length > 0 ? (
                            <VisibilityOutlinedIcon
                              style={{
                                color: Colors?.bg_blue,
                                cursor: "pointer",
                                fontSize: "1.5rem",
                              }}
                              onClick={() => {
                                setViewBagsForItem(row.bags);
                                setShowViewBagsModal(true);
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </div>
                      )}
                    </td>

                    {/* Rate */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={displayData.rate || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numberRegex = /^\d*\.?\d*$/;
                            if (value === "" || numberRegex.test(value)) {
                              handleEditFieldChange("rate", value);
                            }
                          }}
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.rate || "-"}</span>
                      )}
                    </td>

                    {/* Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.amount || "-"}</span>
                    </td>

                    {/* Discount */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "flex-start",
                          }}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={displayData.discount || ""}
                            // onChange={(e) =>
                            //     handleEditFieldChange("discount", e.target.value)
                            // }
                            onChange={(e) => {
                              const value = e.target.value;
                              const numberRegex = /^\d*\.?\d*$/;

                              if (value === "" || numberRegex.test(value)) {
                                handleEditFieldChange("discount", value);
                              }
                            }}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              flex: 1,
                              "& .MuiInputBase-root": {
                                height: "32px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            }}
                          />
                          <Autocomplete
                            size="small"
                            options={["%", "₹"]}
                            disableClearable
                            value={displayData.discount_type || "%"}
                            onChange={(_, value) =>
                              handleEditFieldChange("discount_type", value)
                            }
                            renderOption={(props, option) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (opt) => opt?.toString() || ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                sx={{
                                  ...autocompleteDropdownStyles.inputSx,
                                  width: "80px",
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 12px",
                                    height: "20px",
                                  },
                                }}
                              />
                            )}
                            ListboxProps={{
                              ...autocompleteDropdownStyles.ListboxProps,
                              style: {
                                maxHeight: "200px",
                                overflow: "auto",
                                zIndex: 100,
                              },
                            }}
                            componentsProps={{
                              popper: {
                                style: {
                                  zIndex: 100,
                                },
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    enabled: true,
                                  },
                                ],
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {row?.discount &&
                          parseFloat(String(row.discount)) > 0 ? (
                            <>
                              <span>{row.discount}</span>
                              <span>{row?.discount_type || "-"}</span>
                            </>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Discount Amount */}
                    <td style={tableStyles.cell}>
                      <span>
                        {displayData.discount_amount &&
                        parseFloat(String(displayData.discount_amount)) > 0
                          ? displayData.discount_amount
                          : "-"}
                      </span>
                    </td>

                    {/* GST */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <Autocomplete
                          size="small"
                          options={gstOptions || []}
                          getOptionLabel={(opt) =>
                            opt?.gst_percent ? String(opt.gst_percent) : ""
                          }
                          value={
                            gstOptions.find(
                              (g) =>
                                String(g.gst_percent) ===
                                String(displayData.gst?.gst_percent)
                            ) || null
                          }
                          onChange={(_, value) =>
                            handleEditFieldChange("gst", value)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                "& .MuiInputBase-root": {
                                  height: "32px",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "6px 12px",
                                  height: "20px",
                                },
                              }}
                            />
                          )}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) =>
                                opt?.gst_percent ? String(opt.gst_percent) : ""
                            )
                          }
                          ListboxProps={{
                            ...autocompleteDropdownStyles.ListboxProps,
                            style: {
                              maxHeight: "200px",
                              overflow: "auto",
                              zIndex: 100,
                            },
                          }}
                          componentsProps={{
                            popper: {
                              style: {
                                zIndex: 100,
                              },
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  enabled: true,
                                },
                              ],
                            },
                          }}
                        />
                      ) : (
                        <span>{row?.gst?.gst_percent || "-"}</span>
                      )}
                    </td>

                    {/* GST Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.gst_amount || "-"}</span>
                    </td>

                    {/* Total Amount */}
                    <td style={tableStyles.cell}>
                      <span>{displayData.total_amount || "-"}</span>
                    </td>

                    {/* Remarks */}
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          placeholder="Enter Remark"
                          value={displayData.remark || ""}
                          onChange={(e) =>
                            handleEditFieldChange("remark", e.target.value)
                          }
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <TruncatedTextCell
                          text={row?.remark}
                          maxWidth="300px"
                          maxLines={4}
                          rowIndex={idx}
                          fallbackText="—"
                        />
                      )}
                    </td>

                    {/* Actions */}
                    <td
                      style={
                        isEditing
                          ? tableStyles.actionCellEditing
                          : tableStyles.actionCell
                      }
                      className="sticky-column"
                    >
                      {isEditing ? (
                        <div style={tableStyles.actionButtonContainer}>
                          <SaveCancelButtons
                            onSave={handleSaveEdit}
                            onCancel={handleCancelEdit}
                          />
                        </div>
                      ) : (
                        <div style={tableStyles.actionButtonContainer}>
                          <EditButton
                            onClick={() => handleEditItem(row)}
                            iconClassName="ph-pencil"
                          />
                          <DeleteButton
                            onClick={() => {
                              const currentItems =
                                mainFormik.values.items || [];
                              const itemToDelete = currentItems.find(
                                (item: any) => item.id === row.id
                              );

                              // Remove bags from bagQuantityValidation when deleting item
                              if (
                                itemToDelete?.bags &&
                                itemToDelete.bags.length > 0 &&
                                setBagQuantityValidation &&
                                bagQuantityValidation
                              ) {
                                const updatedBagValidation = [
                                  ...bagQuantityValidation,
                                ];
                                itemToDelete.bags.forEach((bag: any) => {
                                  const bagIndex =
                                    updatedBagValidation.findIndex(
                                      (b) =>
                                        String(b.inventory_item_id) ===
                                        String(bag.inventory_item_id)
                                    );
                                  if (bagIndex !== -1) {
                                    updatedBagValidation[
                                      bagIndex
                                    ].issue_quantity -=
                                      parseFloat(String(bag.issue_quantity)) ||
                                      0;
                                    // Remove if issue_quantity becomes 0 or negative
                                    if (
                                      updatedBagValidation[bagIndex]
                                        .issue_quantity <= 0
                                    ) {
                                      updatedBagValidation.splice(bagIndex, 1);
                                    }
                                  }
                                });
                                setBagQuantityValidation(updatedBagValidation);
                              }

                              const updatedItems = currentItems.filter(
                                (item: any) => item.id !== row.id
                              );
                              mainFormik.setFieldValue("items", updatedItems);

                              // If deleting the item being edited, cancel edit mode
                              if (
                                editingItemId === row.id &&
                                setEditingItemId
                              ) {
                                setEditingItemId(null);
                                setEditingRowData(null);
                                formik.resetForm();
                                setSelectedBags([]);
                              }
                            }}
                            iconClassName="ph-trash"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
      <QuotationBagSelectionModal
        show={showBagModal}
        tableFormik={formik}
        handleClose={() => setShowBagModal(false)}
        quantity={
          editingItemId !== null && editingRowData
            ? parseFloat(editingRowData.quantity) || 0
            : parseFloat(formik.values.quantity) || 0
        }
        onSave={(bags) => {
          // If editing an item, only update editingRowData, not the add form
          if (editingItemId !== null && editingRowData) {
            setEditingRowData({ ...editingRowData, bags });
          } else {
            // Only update add form when not editing
            setSelectedBags(bags);
            formik.setFieldValue("bags", bags);
          }
          setShowBagModal(false);
        }}
        initialBags={
          editingItemId !== null && editingRowData
            ? (editingRowData.bags || []).map((bag: any) => ({
                inventory_item_id: bag.inventory_item_id,
                bag_no: bag.bag_no,
                available_quantity: bag.available_quantity,
                issue_quantity: bag.issue_quantity,
              }))
            : selectedBags
        }
        bagQuantityValidation={bagQuantityValidation || []}
        setBagQuantityValidation={setBagQuantityValidation || (() => {})}
        editingItemId={editingItemId}
        currentItemBags={
          editingItemId !== null
            ? (
                mainFormik?.values?.items?.find(
                  (item: any) => item.id === editingItemId
                )?.bags || []
              ).map((bag: any) => ({
                inventory_item_id: bag.inventory_item_id,
                bag_no: bag.bag_no,
                available_quantity: bag.available_quantity,
                issue_quantity: bag.issue_quantity,
              }))
            : []
        }
        editingRowData={editingRowData}
        warehouseId={warehouseId}
      />
      <QuotationViewBagsModal
        show={showViewBagsModal}
        handleClose={() => setShowViewBagsModal(false)}
        bags={viewBagsForItem}
      />
    </>
  );
};

export default QuotationItemTable;
